import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Switch, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Divider, Stack, CircularProgress, Alert, IconButton, Tooltip, Chip,
} from '@mui/material';
import { Save as SaveIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { notificationConfigApi } from '@/services/api';
import ApproverLinksCard from '@/components/aprovar/ApproverLinksCard';

const API_BASE = (import.meta as any).env?.VITE_API_URL || 'https://api.pay.viptecnologia.com.br/api/v1';

const AprovacoesSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState<any>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await notificationConfigApi.get();
      if (r.success) setCfg(r.data);
    } catch {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k: string, v: any) => setCfg((p: any) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload: any = { ...cfg };
      // não reenvia máscaras
      ['nossozapToken', 'approvalWahaKey', 'approvalWebhookSecret', 'emailSmtpPass'].forEach((k) => {
        if (typeof payload[k] === 'string' && payload[k].startsWith('****')) delete payload[k];
      });
      const r = await notificationConfigApi.update(payload);
      if (r.success) { setCfg(r.data); toast.success('Configuração salva!'); }
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const webhookUrl = `${API_BASE.replace(/\/$/, '')}/external/approvals/whatsapp/${cfg.companyId}`;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700}>Notificações & Aprovações</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Comprovante automático no WhatsApp e aprovação de pagamentos por grupo.
      </Typography>

      {/* Comprovante automático */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Comprovante automático</Typography>
            <Typography variant="body2" color="text.secondary">Envia o PDF do comprovante quando um pagamento é confirmado.</Typography>
          </Box>
          <Switch checked={!!cfg.autoSend} onChange={(e) => set('autoSend', e.target.checked)} />
        </Box>
        {cfg.autoSend && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl size="small" sx={{ maxWidth: 240 }}>
              <InputLabel>Canal</InputLabel>
              <Select label="Canal" value={cfg.provider || 'off'} onChange={(e) => set('provider', e.target.value)}>
                <MenuItem value="nossozap">nossozap (WhatsApp)</MenuItem>
                <MenuItem value="webhook">Webhook (outro CRM)</MenuItem>
                <MenuItem value="off">Desligado</MenuItem>
              </Select>
            </FormControl>
            {cfg.provider === 'nossozap' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField size="small" label="Token nossozap" value={cfg.nossozapToken || ''} onChange={(e) => set('nossozapToken', e.target.value)} fullWidth placeholder="zap_..." />
                <TextField size="small" label="Inbox ID" type="number" value={cfg.nossozapInboxId ?? ''} onChange={(e) => set('nossozapInboxId', e.target.value)} sx={{ maxWidth: 140 }} />
              </Stack>
            )}
            {cfg.provider === 'webhook' && (
              <TextField size="small" label="URL do webhook do seu CRM" value={cfg.webhookUrl || ''} onChange={(e) => set('webhookUrl', e.target.value)} fullWidth placeholder="https://..." />
            )}
          </Stack>
        )}
      </Paper>

      {/* Comprovante por e-mail */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Comprovante por e-mail</Typography>
            <Typography variant="body2" color="text.secondary">Envia o comprovante em PDF anexo, por SMTP, quando o pagamento é confirmado.</Typography>
          </Box>
          <Switch checked={!!cfg.emailEnabled} onChange={(e) => set('emailEnabled', e.target.checked)} />
        </Box>
        {cfg.emailEnabled && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" label="Servidor SMTP" value={cfg.emailSmtpHost || ''} onChange={(e) => set('emailSmtpHost', e.target.value)} fullWidth placeholder="email2.viptecnologia.com.br" />
              <TextField size="small" label="Porta" type="number" value={cfg.emailSmtpPort ?? 587} onChange={(e) => set('emailSmtpPort', e.target.value)} sx={{ maxWidth: 120 }} />
              <FormControl size="small" sx={{ maxWidth: 140 }}>
                <InputLabel>Segurança</InputLabel>
                <Select label="Segurança" value={cfg.emailSmtpSecure ? 'ssl' : 'starttls'} onChange={(e) => set('emailSmtpSecure', e.target.value === 'ssl')}>
                  <MenuItem value="starttls">STARTTLS (587)</MenuItem>
                  <MenuItem value="ssl">SSL (465)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" label="Usuário SMTP" value={cfg.emailSmtpUser || ''} onChange={(e) => set('emailSmtpUser', e.target.value)} fullWidth placeholder="contato@papelaria.vip" />
              <TextField size="small" label="Senha SMTP" type="password" value={cfg.emailSmtpPass || ''} onChange={(e) => set('emailSmtpPass', e.target.value)} fullWidth />
            </Stack>
            <TextField size="small" label="Remetente (From)" value={cfg.emailFrom || ''} onChange={(e) => set('emailFrom', e.target.value)} fullWidth placeholder="contato@papelaria.vip (default: usuário SMTP)" />
          </Stack>
        )}
      </Paper>

      {/* Aprovação por grupo */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Aprovação por grupo de WhatsApp</Typography>
            <Typography variant="body2" color="text.secondary">Posta o pedido num grupo; um master responde “{cfg.approvalKeyword || 'aprovar'}” citando para efetivar.</Typography>
          </Box>
          <Switch checked={!!cfg.approvalEnabled} onChange={(e) => set('approvalEnabled', e.target.checked)} />
        </Box>

        {cfg.approvalEnabled && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" label="ID do grupo (@g.us)" value={cfg.approvalGroupId || ''} onChange={(e) => set('approvalGroupId', e.target.value)} fullWidth placeholder="120363...@g.us" />
              <TextField size="small" label="Palavra-chave" value={cfg.approvalKeyword || ''} onChange={(e) => set('approvalKeyword', e.target.value)} sx={{ maxWidth: 180 }} />
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Duplo check (confirmar antes de pagar)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Ao responder “{cfg.approvalKeyword || 'aprovar'}”, o sistema valida a chave e manda um resumo com o recebedor; só efetiva quando responderem “{cfg.approvalConfirmKeyword || 'confirmar'}”.
                </Typography>
              </Box>
              <Switch checked={cfg.approvalDoubleCheck !== false} onChange={(e) => set('approvalDoubleCheck', e.target.checked)} />
            </Box>
            {cfg.approvalDoubleCheck !== false && (
              <TextField
                size="small" label="Palavra de confirmação" value={cfg.approvalConfirmKeyword || ''}
                onChange={(e) => set('approvalConfirmKeyword', e.target.value)} sx={{ maxWidth: 220 }} placeholder="confirmar"
              />
            )}
            <TextField
              size="small" label="Masters autorizados (um por linha: JID ou LID)" multiline minRows={2}
              value={(cfg.approvalMasters || []).join('\n')}
              onChange={(e) => set('approvalMasters', e.target.value.split('\n').map((s: string) => s.trim()).filter(Boolean))}
              fullWidth placeholder={'553791265314@c.us\n82527964397640@lid'}
            />

            <Divider><Chip label="Comprovante após aprovar" size="small" /></Divider>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Enviar comprovante no WhatsApp ao aprovar</Typography>
                <Typography variant="caption" color="text.secondary">Manda o PDF + link de validação assim que o pagamento é efetivado.</Typography>
              </Box>
              <Switch checked={cfg.approvalReceiptEnabled !== false} onChange={(e) => set('approvalReceiptEnabled', e.target.checked)} />
            </Box>
            {cfg.approvalReceiptEnabled !== false && (
              <Stack spacing={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Enviar para</InputLabel>
                  <Select label="Enviar para" value={cfg.approvalReceiptDestination || 'group'} onChange={(e) => set('approvalReceiptDestination', e.target.value)}>
                    <MenuItem value="group">Grupo de trabalho (o mesmo da aprovação)</MenuItem>
                    <MenuItem value="approver">Privado de quem aprovou</MenuItem>
                    <MenuItem value="number">Número específico</MenuItem>
                  </Select>
                </FormControl>
                {cfg.approvalReceiptDestination === 'number' && (
                  <TextField size="small" label="Número (com DDD)" value={cfg.approvalReceiptNumber || ''} onChange={(e) => set('approvalReceiptNumber', e.target.value)} fullWidth placeholder="5537999999999" />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Também enviar o número do fornecedor</Typography>
                    <Typography variant="caption" color="text.secondary">Posta o contato do fornecedor (se veio na solicitação) para a pessoa encaminhar o comprovante.</Typography>
                  </Box>
                  <Switch checked={cfg.approvalForwardSupplier !== false} onChange={(e) => set('approvalForwardSupplier', e.target.checked)} />
                </Box>
              </Stack>
            )}

            <Alert severity="info" sx={{ '& a': { color: 'inherit' } }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                Configure este <strong>webhook do grupo</strong> no agenthub (forward_webhook_url) com o secret abaixo:
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{webhookUrl}</code>
                <Tooltip title="Copiar">
                  <IconButton size="small" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Copiado'); }}>
                    <CopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Alert>

            <Divider><Chip label="Avançado" size="small" /></Divider>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" label="WAHA URL" value={cfg.approvalWahaUrl || ''} onChange={(e) => set('approvalWahaUrl', e.target.value)} fullWidth placeholder="https://waha..." />
              <TextField size="small" label="Sessão WAHA" value={cfg.approvalWahaSession || ''} onChange={(e) => set('approvalWahaSession', e.target.value)} sx={{ maxWidth: 220 }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" label="WAHA API Key" type="password" value={cfg.approvalWahaKey || ''} onChange={(e) => set('approvalWahaKey', e.target.value)} fullWidth />
              <TextField size="small" label="Webhook Secret" type="password" value={cfg.approvalWebhookSecret || ''} onChange={(e) => set('approvalWebhookSecret', e.target.value)} fullWidth />
            </Stack>
          </Stack>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={save} disabled={saving}>
          Salvar
        </Button>
      </Box>

      <ApproverLinksCard />
    </Box>
  );
};

export default AprovacoesSettingsPage;
